// ProfilePeekDrawer
// Slides a user's full profile in over whatever surface you're on, so a
// "View" tap never costs you your place. Used by the Keep Pile (review a kept
// card before playing it) and the Matches feed (size someone up before
// liking), and generic enough for any list that shows profiles.

import React, { useCallback, useEffect, useState } from 'react';
import { ProfileView } from '../../Profile/ProfileView/ProfileView';
import { ProfileSetupData, ProfileEditorData } from '../../Profile/types';
import { profileService } from '../../../services/Profile/profileService';
import { messagingService } from '../../../services/Messaging/messagingService';
import { safetyService } from '../../../services/Safety/safetyService';
import { useProfileSafety } from '../../../hooks/shared/useProfileSafety';
import { useAuthContext } from '../../../providers';
import { useSubscriptionContext } from '../../../providers/SubscriptionProvider';
import { logger } from '../../../utils/logger';
import {
  PeekBackdrop,
  PeekDrawer,
  PeekHeader,
  PeekTitleGroup,
  PeekTitle,
  PeekSubtitle,
  PeekCloseButton,
  PeekBody,
  PeekStatus,
  PeekSpinner,
  PeekRetryButton,
  PeekFooter,
  PeekFooterButton,
  PeekComposer,
  PeekComposerInput,
  PeekComposerRow,
  PeekComposerHint
} from '../../../styles/shared/ProfilePeekDrawer.styles';

// Matches the slide transition in ProfilePeekDrawer.styles so the panel is only
// unmounted after it has animated off-screen.
const CLOSE_ANIMATION_MS = 320;

/** The profile being previewed. Null closes the drawer. */
export interface ProfilePeekTarget {
  profileId: string;
  firstName: string;
  age?: number;
  compatibilityScore?: number;
  /** Context line under the name, e.g. "From your keep pile". */
  context?: string;
}

export interface ProfilePeekAction {
  key: string;
  label: string;
  variant: 'primary' | 'secondary' | 'danger';
  onClick: () => void;
  ariaLabel?: string;
  /** Close the drawer after the action fires. Defaults to true. */
  closeOnClick?: boolean;
}

interface ProfilePeekDrawerProps {
  target: ProfilePeekTarget | null;
  onClose: () => void;
  /** Footer buttons, rendered after the built-in back button. */
  actions?: ProfilePeekAction[];
  /** Label for the built-in back button, e.g. "Keep Pile" or "Matches". */
  backLabel?: string;
  onNotify?: (type: 'match' | 'request' | 'info' | 'warning', message: string) => void;
  /**
   * This person was blocked. The host should drop them from whatever list it
   * is showing — the block is recorded either way, but a profile that stays on
   * screen after you block it does not look blocked.
   */
  onBlocked?: (profileId: string) => void;
}

const toProfileSetupData = (data: ProfileEditorData): ProfileSetupData => ({
  basicInfo: data.basicInfo,
  photos: data.photos.map(photo => ({
    ...photo,
    isValidated: photo.validationStatus === 'approved' || photo.isValidated || false
  })),
  bio: data.bio,
  interests: data.interests,
  preferences: data.preferences,
  currentStep: 5,
  isComplete: true,
  verificationStatus: 'verified'
});

export const ProfilePeekDrawer: React.FC<ProfilePeekDrawerProps> = ({
  target,
  onClose,
  actions = [],
  backLabel = 'Back',
  onNotify,
  onBlocked
}) => {
  // The target currently rendered — held one beat longer than `target` so the
  // panel can animate out instead of vanishing.
  const [rendered, setRendered] = useState<ProfilePeekTarget | null>(target);
  const [isOpen, setIsOpen] = useState(false);
  const [profileData, setProfileData] = useState<ProfileSetupData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  // Quick composer state. Premium members send from here rather than being
  // sent to /messaging, which would undo the drawer's whole purpose.
  const [composerOpen, setComposerOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  // Blocking is available from every surface that opens this drawer — the
  // feeds, the Keep Pile and the chat rooms — because it belongs wherever a
  // profile is shown, not only inside a conversation you may never have had.

  // Reporting belongs where you encounter someone, not on a settings page you
  // have to go and find afterwards.


  const { user } = useAuthContext();
  const currentUserId = user?.id ?? '';
  const { isPremium } = useSubscriptionContext();

  const profileId = target?.profileId;

  // Drive the slide-in / slide-out transition.
  useEffect(() => {
    if (target) {
      setRendered(target);
      const frame = requestAnimationFrame(() => setIsOpen(true));
      return () => cancelAnimationFrame(frame);
    }

    setIsOpen(false);
    const timer = setTimeout(() => {
      setRendered(null);
      setProfileData(null);
      setError(null);
    }, CLOSE_ANIMATION_MS);
    return () => clearTimeout(timer);
  }, [target]);

  // Load the full profile.
  useEffect(() => {
    if (!profileId) return;

    let cancelled = false;
    setIsLoading(true);
    setError(null);
    setProfileData(null);

    profileService.getProfile(profileId)
      .then(data => {
        if (!cancelled) setProfileData(toProfileSetupData(data));
      })
      .catch(err => {
        logger.error('Failed to load profile for peek drawer:', err);
        if (!cancelled) setError('Could not load this profile. Please try again.');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => { cancelled = true; };
  }, [profileId, reloadToken]);

  // Escape closes the drawer, not whatever is behind it.
  useEffect(() => {
    if (!target) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [target, onClose]);

  // ProfileView's Message button navigates to /messaging by default, which is
  // exactly the jump this drawer exists to avoid. It used to answer with
  // "chatting opens up once you match" for everyone — which is not the rule
  // and never has been: the server allows any Message Bet, and allows a
  // subscriber to message anyone. So a Premium member was told to wait for a
  // match to use the feature they had paid for.
  const handleMessage = useCallback(() => {
    if (isPremium) {
      setComposerOpen(true);
      return;
    }
    onNotify?.(
      'info',
      'Premium lets you message anyone. Without it you can still chat in any Message Bet.',
    );
  }, [isPremium, onNotify]);

  const handleSend = useCallback(async () => {
    const body = draft.trim();
    if (!body || !rendered || !currentUserId || sending) return;

    setSending(true);
    try {
      // create_conversation_if_not_exists on the server, so sending twice
      // continues the same thread rather than starting a second one.
      const conversation = await messagingService.createConversation([
        currentUserId,
        rendered.profileId,
      ]);
      await messagingService.sendMessage({
        conversationId: conversation.id,
        senderId: currentUserId,
        recipientId: rendered.profileId,
        content: body,
        messageType: 'text',
        isRead: false,
      } as any);

      setDraft('');
      setComposerOpen(false);
      onNotify?.('info', `Message sent to ${rendered.firstName}.`);
    } catch (error: any) {
      logger.error('Failed to send message from profile peek:', error);
      // The server's own words where it has any: PREMIUM_REQUIRED carries an
      // explanation worth more than a generic failure.
      const message = typeof error?.message === 'string' && error.message.includes('PREMIUM_REQUIRED')
        ? 'Messaging outside a Message Bet needs Premium.'
        : "That message couldn't be sent. Please try again.";
      onNotify?.('warning', message);
    } finally {
      setSending(false);
    }
  }, [draft, rendered, currentUserId, sending, onNotify]);

  // A draft belongs to the person it was written for. Clearing on target
  // change stops half a message following the reader to the next profile.
  useEffect(() => {
    setComposerOpen(false);
    setDraft('');
    // Report and block state is reset by useProfileSafety, on the same key.
  }, [target?.profileId]);

  // Shared with the full profile page, so "block" means the same thing and
  // behaves the same way wherever a profile is shown.
  const {
    reporting,
    setReporting,
    reportType,
    setReportType,
    submittingReport,
    confirmingBlock,
    setConfirmingBlock,
    blocking,
    block: handleBlock,
    submitReport: handleReport,
    reasons: reportReasons,
  } = useProfileSafety({
    profileId: rendered?.profileId ?? null,
    firstName: rendered?.firstName ?? 'this person',
    onNotify,
    onBlocked,
    onDone: onClose,
  });

  const handleAction = useCallback((action: ProfilePeekAction) => {
    action.onClick();
    if (action.closeOnClick !== false) onClose();
  }, [onClose]);

  if (!rendered) return null;

  const { firstName, age, compatibilityScore, context } = rendered;
  const subtitle = [
    compatibilityScore != null ? `${compatibilityScore}% Match` : null,
    context,
  ].filter(Boolean).join(' · ');

  return (
    <>
      <PeekBackdrop $isOpen={isOpen} onClick={onClose} />
      <PeekDrawer
        $isOpen={isOpen}
        role="dialog"
        aria-modal="true"
        aria-label={`${firstName}'s profile preview`}
        // A modal sitting over the feed. Swiping inside it must not change the
        // tab underneath, which the viewer cannot even see.
        data-no-tab-swipe
      >
        <PeekHeader>
          <PeekTitleGroup>
            <PeekTitle>{firstName}{age != null ? `, ${age}` : ''}</PeekTitle>
            {subtitle && <PeekSubtitle>{subtitle}</PeekSubtitle>}
          </PeekTitleGroup>
          <PeekCloseButton onClick={onClose} aria-label="Close profile preview">
            ×
          </PeekCloseButton>
        </PeekHeader>

        <PeekBody>
          {isLoading && (
            <PeekStatus>
              <PeekSpinner />
              <span>Loading {firstName}'s profile...</span>
            </PeekStatus>
          )}

          {!isLoading && error && (
            <PeekStatus>
              <span>{error}</span>
              <PeekRetryButton onClick={() => setReloadToken(t => t + 1)}>
                Try Again
              </PeekRetryButton>
            </PeekStatus>
          )}

          {!isLoading && !error && profileData && (
            <ProfileView
              profileData={profileData}
              isOwnProfile={false}
              targetUserId={rendered.profileId}
              onMessage={handleMessage}
              // The footer below already carries Report and Block.
              showSafetyActions={false}
            />
          )}
        </PeekBody>

        <PeekFooter>
          {composerOpen ? (
            <PeekComposer>
              <PeekComposerInput
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder={`Message ${firstName}…`}
                maxLength={1000}
                disabled={sending}
                autoFocus
                onKeyDown={(e) => {
                  // Enter sends, Shift+Enter breaks the line — the convention
                  // every messaging surface in the app already follows.
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
              />
              <PeekComposerRow>
                <PeekComposerHint>
                  {sending ? 'Sending…' : 'Enter to send'}
                </PeekComposerHint>
                <PeekFooterButton
                  $variant="secondary"
                  onClick={() => { setComposerOpen(false); setDraft(''); }}
                  disabled={sending}
                >
                  Cancel
                </PeekFooterButton>
                <PeekFooterButton
                  $variant="primary"
                  onClick={handleSend}
                  disabled={sending || draft.trim().length === 0}
                >
                  Send
                </PeekFooterButton>
              </PeekComposerRow>
            </PeekComposer>
          ) : (
            <>
          {reporting ? (
            <PeekComposer>
              <PeekComposerHint style={{ marginRight: 0 }}>
                What is wrong with {firstName}'s profile or behaviour?
              </PeekComposerHint>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {reportReasons.map((reason) => (
                  <PeekFooterButton
                    key={reason.type}
                    $variant={reportType === reason.type ? 'primary' : 'secondary'}
                    onClick={() => setReportType(reason.type)}
                    style={{ flex: '0 0 auto' }}
                  >
                    {reason.label}
                  </PeekFooterButton>
                ))}
              </div>
              <PeekComposerRow>
                <PeekComposerHint>
                  Reports are private. {firstName} is not told.
                </PeekComposerHint>
                <PeekFooterButton
                  $variant="secondary"
                  onClick={() => { setReporting(false); setReportType(null); }}
                  disabled={submittingReport}
                >
                  Cancel
                </PeekFooterButton>
                <PeekFooterButton
                  $variant="danger"
                  onClick={handleReport}
                  disabled={submittingReport || !reportType}
                >
                  {submittingReport ? 'Sending…' : 'Send report'}
                </PeekFooterButton>
              </PeekComposerRow>
            </PeekComposer>
          ) : confirmingBlock ? (
            // Asked before it happens, and in words that say what it does.
            // Blocking is mutual and silent: neither of you can reach the
            // other afterwards, and they are not told.
            <PeekComposer>
              <PeekComposerHint style={{ marginRight: 0 }}>
                Block {firstName}? Neither of you will be able to message or
                see the other. They are not told.
              </PeekComposerHint>
              <PeekComposerRow>
                <PeekFooterButton
                  $variant="secondary"
                  onClick={() => setConfirmingBlock(false)}
                  disabled={blocking}
                >
                  Cancel
                </PeekFooterButton>
                <PeekFooterButton
                  $variant="danger"
                  onClick={handleBlock}
                  disabled={blocking}
                >
                  {blocking ? 'Blocking…' : `Block ${firstName}`}
                </PeekFooterButton>
              </PeekComposerRow>
            </PeekComposer>
          ) : (
            <>
          <PeekFooterButton $variant="secondary" onClick={onClose}>
            ← Back to {backLabel}
          </PeekFooterButton>
          {actions.map(action => (
            <PeekFooterButton
              key={action.key}
              $variant={action.variant}
              onClick={() => handleAction(action)}
              aria-label={action.ariaLabel}
            >
              {action.label}
            </PeekFooterButton>
          ))}
          <PeekFooterButton
            $variant="secondary"
            onClick={() => setReporting(true)}
            aria-label={`Report ${firstName}`}
          >
            Report
          </PeekFooterButton>
          <PeekFooterButton
            $variant="danger"
            onClick={() => setConfirmingBlock(true)}
            aria-label={`Block ${firstName}`}
          >
            Block
          </PeekFooterButton>
            </>
          )}
            </>
          )}
        </PeekFooter>
      </PeekDrawer>
    </>
  );
};
